import { useEffect, ChangeEvent, Key, ReactElement, ReactPortal, JSXElementConstructor, ReactNode, } from 'react';
import { Outlet } from 'react-router-dom';
import { CustomerHeader, CustomerSideBar, LiveChatButton, ChatOptions } from '../components';
import { Box, TextField, IconButton } from "@mui/material";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';
import { AppButton } from "../components";
import { SupervisorService } from '../services';
import { useAuth } from '../hooks/useAuth';
import { useLiveChat } from '../hooks/useLiveChat';
import { useChatbot } from '../hooks/useChatbot';
import { useFileAttachments } from '../hooks/useFileAttachments';
import { mergeConvo, maxPreviewFiles } from '../utils/chatUtils';
import _ from 'lodash';
import {
  chatConversationData as defaultData,
  ActionResponse,
  GoToNodePayload,
  Option,
  TableData,
} from '../types/chatBot';
import {
  LiveChatMainBox,
  // Chatbot styles
  ChatBotBox,
  ChatBotHeader,
  ChatCompanyLogo,
  ChatCompanyName,
  ChatBotBody,
  ChatMsgListing,
  ChatMssList,
  ChatMssListBox,
  ChatMsg,
  ChatMsgOptions,
  ChatBotFooter,
  ChatFooterOptions,
  ChatAttachments,
  ChatAttachmentCol
} from "../pages/Customer/styles/Dashboard.styles";
import CustomerService from '../services/Customer.Service';



const CustomerLayout: React.FC = () => {
  const user = useAuth();

  // Use custom hooks
  const { isActive, chatMode, setChatMode, chatbtnClick, handleBackToOptions } = useLiveChat();

  const {
    chatBodyRef, companyDetails, setCompanyDetails, formData, setFormData,
    currentKey, setCurrentKey, conversationData, setConversationData,
    messages, setMessages, input, setInput, error, setError
  } = useChatbot();

  const { attachError, setAttachError, attachments, setAttachments, handleFileChange } = useFileAttachments();

  const handleChatModeSelect = (mode: 'ai' | 'agent') => {
    setChatMode(mode);
    // Reset chatbot to start with pre-populated user data
    setCurrentKey('queryType');

    // Create personalized welcome message
    const welcomeText = `Hello ${user?.firstName || 'there'}! I'm your virtual assistant. How can I help you today?`;

    setMessages([{
      sender: 'bot',
      text: welcomeText,
      options: conversationData['queryType']?.options
    }]);
    // Pre-populate with logged-in user data
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || ''
    });
    setInput('');
    setAttachments([]);
    setError('');
    setAttachError('');
  };

  // Chatbot input handlers
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setError('');
    setAttachError('');
    setInput(e.target.value);
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatBodyRef.current) {
      const el = chatBodyRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Load company data and chatbot configuration - Only call API once
  useEffect(() => {
    const loadCompanyData = async () => {
      if (user && !companyDetails) {
        try {
          // Get company details only once
          const response = await SupervisorService.getCompanyDetails();
          const company = response.data;
          setCompanyDetails(company);

          // Pre-populate form data with logged-in user information
          setFormData({
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || ''
          });

          // Use company's chatBotQuestions if available, otherwise use chatbot endpoint
          if (company.chatBotQuestions) {
            // Use company's custom questions directly
            const mergedData = mergeConvo(defaultData, company.chatBotQuestions);
            setConversationData(mergedData);

            // Create personalized welcome message
            const welcomeText = `Hello ${user?.firstName || 'there'}! I'm your virtual assistant. How can I help you today?`;

            setMessages([{
              sender: 'bot',
              text: welcomeText,
              options: mergedData[currentKey]?.options
            }]);
          } else if (company.chatbotId) {
            try {
              // Fallback to chatbot endpoint if no custom questions
              const chatbotResponse = await SupervisorService.checkChatbotByUUID({ id: company.chatbotId });
              const mergedData = mergeConvo(defaultData, chatbotResponse.data.company.chatBotQuestions || {});
              setConversationData(mergedData);

              // Create personalized welcome message
              const welcomeText = `Hello ${user?.firstName || 'there'}! I'm your virtual assistant. How can I help you today?`;

              setMessages([{
                sender: 'bot',
                text: welcomeText,
                options: mergedData[currentKey]?.options
              }]);
            } catch (error) {
              console.error("Error loading chatbot configuration:", error);
            }
          }
        } catch (error) {
          console.error("Error loading company details:", error);
        }
      }
    };

    loadCompanyData();
  }, [user, companyDetails]); // Only depend on user and companyDetails - prevents repeated calls

  // Separate useEffect for updating messages when currentKey changes
  useEffect(() => {
    // Update messages when currentKey changes (but only if company details are loaded)
    if (companyDetails && Object.keys(conversationData).length > 0) {
      const currentNode = conversationData[currentKey];
      if (currentNode && currentNode.text && currentKey !== 'queryType') {
        // Don't override queryType since we have a personalized welcome message
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1];
          if (lastMessage?.sender === 'bot' && lastMessage?.text === currentNode.text) {
            return prev; // Don't duplicate the same message
          }
          return [{
            sender: 'bot',
            text: currentNode.text || '',
            options: currentNode.options
          }];
        });
      }
    }
  }, [currentKey, companyDetails, conversationData]); // Separate effect for message updates

  // Chatbot navigation logic
  const goToNode = async (payload: GoToNodePayload) => {
    const { userValue = '', extraData = {}, key, attachments: fileAttachments, reset } = payload;

    if (reset) {
      setFormData((fd) => ({
        firstName: user?.firstName || fd.firstName,
        lastName: user?.lastName || fd.lastName,
        email: user?.email || fd.email,
        organisationName: fd.organisationName
      }));
    }

    if (userValue) {
      setFormData((fd: any) => ({ ...fd, [currentKey]: userValue }));
    }

    if (userValue && userValue !== '') {
      setMessages((m: any) => [...m, {
        sender: 'user',
        text: userValue,
        attachments: Number(fileAttachments?.length) > 0 ? fileAttachments : undefined
      }]);
    }

    const node = conversationData[key];
    if (!node) {
      console.error(`Node with key "${key}" not found in conversation data`);
      return;
    }

    let res: ActionResponse = {};

    if (node.action) {
      const chatbotId = companyDetails?.chatbotId;
      if (!chatbotId) {
        console.error('ChatbotId is missing. Company details may not be loaded yet.');
        setMessages((m: any) => [...m, {
          sender: 'bot',
          text: 'Please wait while we set up your chat session...',
        }]);
        return;
      }

      try {
        res = await node.action(
          { ...formData, chatbotId, input },
          userValue,
          { ...extraData, attachments: fileAttachments }
        );
        setMessages((m: any) => [...m, {
          sender: 'bot',
          text: res?.text || node?.text || '',
          options: res?.options,
          table: res?.table
        }]);
        if (res.next || node.next) {
          goToNode({ key: res.next || node.next!, userValue: '', reset: true });
        }
      } catch (error) {
        console.error("Error executing chatbot action:", error);
        setMessages((m: any) => [...m, {
          sender: 'bot',
          text: 'Sorry, I encountered an error processing your request. Please try again.',
        }]);
      }
    } else {
      setMessages((m: any) => [...m, {
        sender: 'bot',
        text: node?.text || '',
        options: node?.options
      }]);
    }

    setCurrentKey(key);

    if (!node?.input && !res?.options?.length && !node?.options?.length && node?.next && !node?.action && !res?.table) {
      goToNode({ key: node.next, userValue: '', reset: true });
    }
  };

  // Handle option selection
  const handleSelect = async (opt: Option | TableData) => {
    const updated = { ...formData, [currentKey]: opt.text, data: opt.data };
    if (opt.data) setFormData(updated);
    if (opt.action) {
      const chatbotId = companyDetails?.chatbotId;
      if (!chatbotId) {
        console.error('ChatbotId is missing. Company details may not be loaded yet.');
        setMessages((m: any) => [...m, {
          sender: 'bot',
          text: 'Please wait while we set up your chat session...',
        }]);
        return;
      }

      try {
        await opt.action({ ...updated, chatbotId, input, data: opt.data }, opt);
      } catch (error) {
        console.error("Error executing option action:", error);
        setMessages((m: any) => [...m, {
          sender: 'bot',
          text: 'Sorry, I encountered an error processing your selection. Please try again.',
        }]);
        return;
      }
    }
    goToNode({ key: opt.next, userValue: opt?.text || opt?.data?.ticketId, extraData: opt });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return setError('This field is required');
    if (currentKey === 'email' && !/^\S+@\S+\.\S+$/.test(input.trim())) {
      return setError('Invalid email format');
    }
    setError('');

    const nextKey = conversationData[currentKey]?.next!;
    if (nextKey) {
      if (currentKey === "sales" || currentKey === "query") {
        setMessages((m: any) => [...m, {
          sender: 'user',
          text: input,
        }]);
        setInput('');
        const res = await CustomerService.SalesTalkWithAiChat({ question: input });
        // setMessages((m: any) => [...m, {
        //   sender: 'bot',
        //   text: res?.text || '',
        //   options: res?.options,
        //   table: res?.table
        // }]);
        // console.log(res, "res");
        if (res && res?.statusCode === 200) {
          if (res?.data?.escalated) {
            setMessages((m: any) => [...m, {
              sender: 'bot',
              text: res?.data?.answer || '',
              options: res?.data?.options,
              table: res?.table
            }, {
              sender: "bot",
              text: res?.data?.message,
              options: [{
                text: "Yes",
                action: async (data, _) => {
                  return await CustomerService.genearteSalesRequest({ chatbotId: data?.chatbotId, queryType: data?.queryType?.toLowerCase(), subject: input });
                },
                next: "salesThanks"
              }, {
                text: "No",
                next: "thanks"
              }]
            }]);
            return;
          }
          setMessages((m: any) => [...m, {
            sender: 'bot',
            text: res?.data?.answer || '',
            options: res?.data?.options,
            table: res?.table
          }]);
        }
      } else {
        goToNode({
          key: nextKey,
          userValue: input,
          attachments: attachments.length ? [...attachments] : undefined
        });
      }
    }
    setInput('');
    setAttachments([]);
  };

  const node = conversationData[currentKey];
  const lastMsgIndex = messages.length - 1;

  // console.log(messages, "messages", currentKey, "currentKey", conversationData, "conversationData");

  return (
    <Box sx={{ display: 'flex', flexFlow: 'column', height: '100vh' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', backgroundColor: '#F0F0F0', height: 1 }}>
        <CustomerSideBar />
        <Box sx={{ display: 'block', flex: 1, maxHeight: 1, maxWidth: 'calc(100% - 248px)' }}>
          <CustomerHeader />
          <Box sx={{ display: 'block', flex: 1, px: 3.75, py: 2.5, maxHeight: 'calc(100% - 75px)', overflow: 'auto' }}>
            {<Outlet />}
          </Box>
        </Box>
      </Box>

      {/* Global Floating Chat */}
      {user?.featureAvailability?.ai_customer && <Box sx={{ position: 'fixed', bottom: 10, right: 10, zIndex: 999 }}>
        <LiveChatButton isActive={isActive} onClick={chatbtnClick} />
        {!isActive && (
          <LiveChatMainBox>
            {chatMode === 'options' ? (
              <ChatOptions
                onSelectMode={handleChatModeSelect}
                customerName={user?.firstName}
              />
            ) : (
              <>
                <ChatBotBox>
                  <ChatBotHeader>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <IconButton
                        size="small"
                        onClick={handleBackToOptions}
                        sx={{ mr: 1 }}
                      >
                        <img src="/images/arrow-left.svg" alt="Back" style={{ width: 20, height: 20 }} />
                      </IconButton>
                      <ChatCompanyLogo>
                        <img src={`${companyDetails?.shortLogoUrl || '/images/static/company-logo.png'}`} alt="Logo" />
                      </ChatCompanyLogo>
                      <ChatCompanyName>
                        {chatMode === 'ai' ? 'AI Assistant' : 'Live Agent'} - {companyDetails?.name || 'Company Name'}
                      </ChatCompanyName>
                    </Box>
                  </ChatBotHeader>

                  <ChatBotBody ref={chatBodyRef}>
                    <ChatMsgListing>
                      {messages.map((m, i: number) => {
                        const isCurrent = i === lastMsgIndex;
                        return (
                          <ChatMssList key={i} className={m.sender === 'user' ? 'user' : ''}>
                            <ChatCompanyLogo className="userImage">
                              <img
                                src={
                                  m.sender === 'user'
                                    ? '/images/default-user.svg'
                                    : companyDetails?.shortLogoUrl || '/images/static/company-logo.png'
                                }
                                alt={m.sender}
                              />
                            </ChatCompanyLogo>
                            <ChatMssListBox>
                              <ChatMsg>{m.text}</ChatMsg>
                              {m.sender === 'bot' && (
                                <>
                                  {m?.options?.map((o: Option | TableData) =>
                                    <ChatMsgOptions key={o.text} onClick={() => isCurrent && handleSelect(o)}>
                                      {o.text}
                                    </ChatMsgOptions>
                                  )}
                                  {m.table && (
                                    <Box sx={{ padding: 1, paddingTop: 0 }}>
                                      <Box sx={{ border: '1px solid #ddd', borderRadius: '8px', maxWidth: '600px', overflowX: 'auto' }}>
                                        <table className="chatMsgTable">
                                          <thead>
                                            <tr>{m.table.cols.map((col: { accessor: Key | null | undefined; header: string | number | bigint | boolean | ReactElement<unknown, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | Promise<string | number | bigint | boolean | ReactPortal | ReactElement<unknown, string | JSXElementConstructor<any>> | Iterable<ReactNode> | null | undefined> | null | undefined; }) => (<th key={col.accessor}>{col.header}</th>))}</tr>
                                          </thead>
                                          <tbody>
                                            {m.table.data?.map((td: Option | TableData, idx: Key | null | undefined) => (
                                              <tr key={idx} onClick={() => isCurrent && handleSelect(td)} style={{ cursor: 'pointer' }}>
                                                {m?.table?.cols?.map((col) => (
                                                  <td key={col.accessor}>
                                                    <span>{col.render ? col.render(td) : (col?.accessor !== undefined ? _.get(td, col.accessor) : '')}</span>
                                                  </td>
                                                ))}
                                              </tr>
                                            ))}
                                            {m.table.data?.length === 0 && (<ChatMsgOptions>No data available</ChatMsgOptions>)}
                                          </tbody>
                                        </table>
                                      </Box>
                                    </Box>
                                  )}
                                </>
                              )}
                            </ChatMssListBox>
                            {Number(m.attachments?.length) > 0 && (
                              <ChatMssListBox sx={{ background: 'transparent !important', width: '100%', marginTop: "8px" }}>
                                <ChatAttachments>
                                  {m?.attachments?.map((file: any, idx: any) => {
                                    const isImage = file.type.startsWith('image/');
                                    return (
                                      <ChatAttachmentCol key={idx}>
                                        {isImage ? (
                                          <img
                                            src={URL.createObjectURL(file)}
                                            alt={file.name}
                                            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                          />
                                        ) : (
                                          <DescriptionIcon sx={{ fontSize: 40 }} />
                                        )}
                                      </ChatAttachmentCol>
                                    );
                                  })}
                                </ChatAttachments>
                              </ChatMssListBox>
                            )}
                          </ChatMssList>
                        );
                      })}
                    </ChatMsgListing>
                  </ChatBotBody>

                  {node.input && node.next && (
                    <ChatBotFooter>
                      <form onSubmit={handleSubmit}>
                        <TextField
                          variant="outlined"
                          placeholder="Enter your message"
                          name="userInput"
                          // multiline
                          // rows={1}
                          fullWidth
                          size="small"
                          value={input}
                          onChange={handleInputChange}
                          error={Boolean(error || attachError)}
                          helperText={error || attachError}
                          type={node.input}
                        />
                        {node.attachment && (
                          <ChatFooterOptions>
                            <IconButton component="label" size="small">
                              <AttachFileIcon />
                              <input type="file" multiple hidden onChange={handleFileChange} />
                            </IconButton>
                          </ChatFooterOptions>
                        )}
                        <IconButton
                          size="small"
                          disabled={(!input.trim() && attachments.length === 0) || Boolean(attachError)}
                          type="submit"
                        >
                          <img src="/images/send-icon.svg" alt="Send" />
                        </IconButton>
                      </form>

                      {attachments.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <ChatAttachments>
                            {attachments.length > maxPreviewFiles ? (
                              <>
                                {attachments.slice(-(maxPreviewFiles - 1)).map((file: any, idx: number) => {
                                  const isImage = file.type.startsWith('image/');
                                  const globalIndex = attachments.length - (maxPreviewFiles - 1) + idx;
                                  return (
                                    <ChatAttachmentCol key={globalIndex}>
                                      <AppButton
                                        size="small"
                                        onClick={() => setAttachments((prev: any[]) => prev.filter((_: any, i: any) => i !== globalIndex))}
                                        tabIndex={-1}
                                        className="close"
                                      >
                                        <img src="/images/cross-icon.svg" alt="Close" />
                                      </AppButton>
                                      {isImage ? (
                                        <img
                                          src={URL.createObjectURL(file)}
                                          alt={file.name}
                                          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                        />
                                      ) : (
                                        <DescriptionIcon sx={{ fontSize: 32, color: '#888' }} />
                                      )}
                                    </ChatAttachmentCol>
                                  );
                                })}
                                <ChatAttachmentCol className="counterOnly">
                                  +{attachments.length - (maxPreviewFiles - 1)}
                                </ChatAttachmentCol>
                              </>
                            ) : (
                              attachments.map((file: any, idx: number) => {
                                const isImage = file.type.startsWith('image/');
                                return (
                                  <ChatAttachmentCol key={idx}>
                                    <AppButton
                                      size="small"
                                      className="close"
                                      onClick={() => setAttachments((prev: any[]) => prev.filter((_: any, i: any) => i !== idx))}
                                      tabIndex={-1}
                                    >
                                      <img src="/images/cross-icon.svg" alt="Close" />
                                    </AppButton>
                                    {isImage ? (
                                      <img
                                        src={URL.createObjectURL(file)}
                                        alt={file.name}
                                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                      />
                                    ) : (
                                      <DescriptionIcon sx={{ fontSize: 32, color: '#888' }} />
                                    )}
                                  </ChatAttachmentCol>
                                );
                              })
                            )}
                          </ChatAttachments>
                        </Box>
                      )}
                    </ChatBotFooter>
                  )}
                </ChatBotBox>
              </>
            )}
          </LiveChatMainBox>
        )}
      </Box>}
    </Box>
  );
};

export default CustomerLayout; 